import { useLocale } from 'hooks/use-locale.hook';
import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { useDispatch, useSelector } from 'react-redux';
import { resourceState } from 'store/bookings/selectors';
import { Price, CurrencyTypeEnum } from 'store/price/types';
import { ACTION_STATUSES } from 'shared/consts';
import { selectAllAddonsPrice } from 'store/add-ons/selectors';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { searchParams } from 'pages/date-select-page/interface';
import { DEFAULT_BOTTOM_PADDING, DEFAULT_DATE_TIME_BLOCK_HEIGHT } from '../constants';
import { applyDiscount } from './utils/applyDiscount';
import { getCurrentHighestDiscount } from 'components/discounts-list/hooks/utils/getCurrentHighestDiscount';
import { changePrice } from 'store/price/actions';
import { usePriceQuery } from 'data/queries/getPriceQuery.hook';

interface UseSelectedDateTimeFilterReturnValues {
  timeRange: { startTime: string; endTime: string; locale?: string };
  price: Price;
  minimumPrice: Price;
  startTime: string;
  endTime: string;
  priceStatus: ACTION_STATUSES;
  locale: string;
  showMinimumPrice: boolean;
  showFreeLabel: boolean;
  containerHeight: number;
  toggleMultipleBookingsVisibility: VoidFunction;
  isMultipleBookingsVisible: boolean;
  reverseAnimation: boolean;
  isMultiDayBooking: boolean;
}

export const useSelectedDateTimeFilter = (
  resourceContainerRef?: React.MutableRefObject<HTMLDivElement>,
): UseSelectedDateTimeFilterReturnValues => {
  const locale = useLocale();
  const dispatch = useDispatch();
  const { startTime, endTime, startDates, startDate } = useSearchParams<searchParams>();
  const timeRange = { startTime: startTime as string, endTime: endTime as string, locale };
  const formattedStartDates = useMemo<Array<string>>(() => startDates?.split(',') || [], [startDates]);
  const { resource } = useSelector(resourceState);
  const isMultiDayBooking = Boolean(startDates);
  const currency = useCurrencyType();

  const priceQuery = usePriceQuery({
    resourceId: resource.id,
    timezone: resource.timezone,
  });

  const freePrice: Price = useMemo(
    () => ({
      price: 0,
      currency: currency as CurrencyTypeEnum,
      is_available: true,
      available_credit: 0,
      used_credit: 0,
      tax_rate: 0,
    }),
    [currency],
  );

  const resourcePrice = isMultiDayBooking ? freePrice : priceQuery?.data?.price;
  const addOnsPrice = useSelector(selectAllAddonsPrice);
  const priceStatus = isMultiDayBooking || priceQuery?.isSuccess ? ACTION_STATUSES.FULFILLED : ACTION_STATUSES.PENDING;
  const minimumPrice = { price: resource.minimum_price, currency: useCurrencyType() } as Price;
  const isPaidResource = !!(
    resource?.minimum_price || resource?.booking_windows?.some(({ pricing_rules }) => pricing_rules?.length)
  );
  const hasCreditTotal = Boolean(resourcePrice?.credit_total && resourcePrice.credit_total > 0);
  const showFreeLabel: boolean = resource?.show_free && !hasCreditTotal;
  const showMinimumPrice: boolean = !resourcePrice && isPaidResource && !!resource?.minimum_price;
  const currentDiscount = getCurrentHighestDiscount(startDate, resource?.discounts);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [isMultipleBookingsVisible, setIsMultipleBookingsVisible] = useState<boolean>(false);
  const [reverseAnimation, setReverseAnimation] = useState<boolean>(true);

  const price = useMemo<Price>(() => {
    const updatedPrice = {
      ...resourcePrice,
      price: (resourcePrice?.price ?? 0) + addOnsPrice,
      ...(Boolean(currentDiscount) && {
        price_with_discount: applyDiscount(resourcePrice?.price ?? 0, currentDiscount?.amount) + addOnsPrice,
      }),
    };

    if (
      Boolean(currentDiscount) &&
      (resourcePrice?.price !== updatedPrice.price ||
        resourcePrice?.price_with_discount !== updatedPrice.price_with_discount)
    ) {
      dispatch(changePrice(updatedPrice));
    }

    return updatedPrice;
  }, [addOnsPrice, currentDiscount, dispatch, resourcePrice]);

  useEffect(() => {
    if (isMultipleBookingsVisible && formattedStartDates.length * DEFAULT_DATE_TIME_BLOCK_HEIGHT !== containerHeight) {
      setContainerHeight(formattedStartDates.length * DEFAULT_DATE_TIME_BLOCK_HEIGHT);
    }
  }, [isMultipleBookingsVisible, formattedStartDates, containerHeight]);

  const toggleMultipleBookingsVisibility = useCallback(() => {
    if (isMultipleBookingsVisible) {
      if (resourceContainerRef?.current) {
        // eslint-disable-next-line no-param-reassign
        resourceContainerRef.current.style.paddingBottom = `${DEFAULT_BOTTOM_PADDING}px`;
      }
      setReverseAnimation(true);

      setTimeout(() => {
        setIsMultipleBookingsVisible(false);
        setContainerHeight(0);
      }, 200);
    } else {
      if (resourceContainerRef?.current) {
        // eslint-disable-next-line no-param-reassign
        resourceContainerRef.current.style.paddingBottom = `${
          formattedStartDates.length * DEFAULT_DATE_TIME_BLOCK_HEIGHT + DEFAULT_BOTTOM_PADDING
        }px`;
      }
      setReverseAnimation(false);
      setContainerHeight(formattedStartDates.length * DEFAULT_DATE_TIME_BLOCK_HEIGHT);
      setIsMultipleBookingsVisible(true);
    }
  }, [formattedStartDates, isMultipleBookingsVisible, resourceContainerRef]);

  return {
    containerHeight,
    isMultipleBookingsVisible,
    toggleMultipleBookingsVisibility,
    timeRange,
    price,
    minimumPrice,
    startTime,
    endTime,
    priceStatus,
    locale,
    showMinimumPrice,
    showFreeLabel,
    reverseAnimation,
    isMultiDayBooking,
  };
};
